import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Power, ChevronDown, ChevronUp, GripVertical, ExternalLink } from 'lucide-react';
import { API_URL } from '../../config';

export default function VideoModelsAdmin() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingModel, setEditingModel] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedModel, setExpandedModel] = useState(null);
  
  // New model form state
  const [newModel, setNewModel] = useState({
    model_id: '',
    label: '',
    key: '',
    price_usd: 0.10,
    price_snaps: 5,
    description: '',
    group: 'Other',
    recommended: false,
    needs_end_frame: false,
    is_active: true,
    is_nsfw: false,
    admin_only: false,
    params: {}
  });

  // Groups for dropdown
  const groups = ['WAN', 'Kling', 'Google', 'Grok', 'Pixverse', 'LTX', 'Minimax', 'Runway', 'Other'];

  useEffect(() => {
    fetchModels();
  }, [showInactive]);

  const fetchModels = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/admin/video-models?include_inactive=${showInactive}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to fetch models');
      
      const data = await res.json();
      setModels(data.models || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/admin/video-models`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newModel)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create model');
      }
      
      setShowAddForm(false);
      setNewModel({
        model_id: '', label: '', key: '', price_usd: 0.10, price_snaps: 5,
        description: '', group: 'Other', recommended: false, needs_end_frame: false,
        is_active: true, is_nsfw: false, admin_only: false, params: {}
      });
      fetchModels();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleUpdate = async (key, updates) => {
    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/admin/video-models/${key}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      
      if (!res.ok) throw new Error('Failed to update model');
      
      setEditingModel(null);
      fetchModels();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDelete = async (key) => {
    if (!window.confirm(`Modell ${key} wirklich löschen?`)) return;
    
    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/admin/video-models/${key}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to delete model');
      
      fetchModels();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleToggle = async (key) => {
    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/admin/video-models/${key}/toggle`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to toggle model');
      
      fetchModels();
    } catch (err) {
      alert(err.message);
    }
  };

  // Generate key from model_id
  const generateKey = (modelId) => {
    const parts = modelId.replace('fal-ai/', '').replace('xai/', '').split('/');
    const keyParts = parts.slice(0, 3).filter(p => !['image-to-video', 'i2v', 'pro'].includes(p));
    return keyParts.join('_').replace(/-/g, '_').replace(/\./g, '_').toLowerCase().slice(0, 30);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#10B981] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">🎬 Video Modelle</h2>
          <p className="text-white/60 text-sm mt-1">FAL.ai Modelle verwalten - Chips erscheinen automatisch im Frontend</p>
        </div>
        
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-white/20 bg-black text-[#10B981] focus:ring-[#10B981]"
            />
            Inaktive zeigen
          </label>
          
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#10B981] text-black font-semibold rounded-xl hover:bg-[#10B981]/90 transition-all"
          >
            <Plus className="w-5 h-5" />
            Neues Modell
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400">
          {error}
        </div>
      )}

      {/* Add New Model Form */}
      {showAddForm && (
        <div className="bg-black/50 border border-[#10B981]/30 rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">➕ Neues FAL Modell hinzufügen</h3>
            <button onClick={() => setShowAddForm(false)} className="text-white/50 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Model ID */}
            <div className="md:col-span-2">
              <label className="block text-white/70 text-sm mb-1">FAL Model ID *</label>
              <input
                type="text"
                value={newModel.model_id}
                onChange={(e) => {
                  const id = e.target.value;
                  setNewModel({...newModel, model_id: id, key: generateKey(id)});
                }}
                placeholder="z.B. fal-ai/kling-video/v4/image-to-video"
                className="w-full px-4 py-3 bg-black border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none"
              />
              <p className="text-white/40 text-xs mt-1">
                Finde Model IDs auf <a href="https://fal.ai/models" target="_blank" rel="noreferrer" className="text-[#10B981] hover:underline">fal.ai/models <ExternalLink className="w-3 h-3 inline" /></a>
              </p>
            </div>

            {/* Label */}
            <div>
              <label className="block text-white/70 text-sm mb-1">Chip Label *</label>
              <input
                type="text"
                value={newModel.label}
                onChange={(e) => setNewModel({...newModel, label: e.target.value})}
                placeholder="z.B. 🎬 Kling 4.0"
                className="w-full px-4 py-3 bg-black border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none"
              />
            </div>

            {/* Key (auto-generated) */}
            <div>
              <label className="block text-white/70 text-sm mb-1">Key (auto)</label>
              <input
                type="text"
                value={newModel.key}
                onChange={(e) => setNewModel({...newModel, key: e.target.value})}
                placeholder="kling_video_v4"
                className="w-full px-4 py-3 bg-black border border-white/20 rounded-xl text-white/70 focus:border-[#10B981] focus:outline-none"
              />
            </div>

            {/* Group */}
            <div>
              <label className="block text-white/70 text-sm mb-1">Gruppe</label>
              <select
                value={newModel.group}
                onChange={(e) => setNewModel({...newModel, group: e.target.value})}
                className="w-full px-4 py-3 bg-black border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none"
              >
                {groups.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>

            {/* Description */}
            <div>
              <label className="block text-white/70 text-sm mb-1">Beschreibung</label>
              <input
                type="text"
                value={newModel.description}
                onChange={(e) => setNewModel({...newModel, description: e.target.value})}
                placeholder="Beste Videoqualität..."
                className="w-full px-4 py-3 bg-black border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none"
              />
            </div>

            {/* Prices */}
            <div>
              <label className="block text-white/70 text-sm mb-1">Preis (USD)</label>
              <input
                type="number"
                step="0.01"
                value={newModel.price_usd}
                onChange={(e) => setNewModel({...newModel, price_usd: parseFloat(e.target.value)})}
                className="w-full px-4 py-3 bg-black border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none"
              />
            </div>
            
            <div>
              <label className="block text-white/70 text-sm mb-1">Preis (Snaps)</label>
              <input
                type="number"
                value={newModel.price_snaps}
                onChange={(e) => setNewModel({...newModel, price_snaps: parseInt(e.target.value)})}
                className="w-full px-4 py-3 bg-black border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none"
              />
            </div>

            {/* Toggles */}
            <div className="md:col-span-2 flex flex-wrap gap-4">
              {[
                {key: 'recommended', label: '⭐ Empfohlen'},
                {key: 'is_active', label: '✅ Aktiv'},
                {key: 'is_nsfw', label: '🔞 NSFW'},
                {key: 'admin_only', label: '🔒 Nur Admin'},
                {key: 'needs_end_frame', label: '🖼️ Braucht End-Frame'}
              ].map(toggle => (
                <label key={toggle.key} className="flex items-center gap-2 text-white/70 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newModel[toggle.key]}
                    onChange={(e) => setNewModel({...newModel, [toggle.key]: e.target.checked})}
                    className="rounded border-white/20 bg-black text-[#10B981] focus:ring-[#10B981]"
                  />
                  {toggle.label}
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
            <button
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 text-white/70 hover:text-white transition-all"
            >
              Abbrechen
            </button>
            <button
              onClick={handleCreate}
              disabled={!newModel.model_id || !newModel.label}
              className="flex items-center gap-2 px-6 py-2 bg-[#10B981] text-black font-semibold rounded-xl hover:bg-[#10B981]/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-5 h-5" />
              Speichern
            </button>
          </div>
        </div>
      )}

      {/* Models List */}
      <div className="space-y-3">
        {models.length === 0 ? (
          <div className="text-center py-12 text-white/50">
            Keine Modelle gefunden
          </div>
        ) : (
          models.map((model) => (
            <div
              key={model.key}
              className={`bg-black/30 border rounded-xl overflow-hidden transition-all ${
                model.is_active ? 'border-white/10' : 'border-red-500/30 opacity-60'
              }`}
            >
              {/* Model Header */}
              <div className="flex items-center gap-4 p-4">
                <div className="text-white/30 cursor-grab">
                  <GripVertical className="w-5 h-5" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold text-white">{model.label}</span>
                    {model.recommended && <span className="text-yellow-400">⭐</span>}
                    {model.is_nsfw && <span className="text-red-400">🔞</span>}
                    {model.admin_only && <span className="text-orange-400">🔒</span>}
                    {!model.is_active && <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">Inaktiv</span>}
                  </div>
                  <div className="text-white/50 text-sm truncate">{model.model_id}</div>
                </div>

                <div className="flex items-center gap-2 text-white/70">
                  <span className="px-2 py-1 bg-white/5 rounded text-sm">{model.group}</span>
                  <span className="px-2 py-1 bg-[#10B981]/20 text-[#10B981] rounded text-sm font-medium">
                    {model.price_snaps} 💎
                  </span>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setExpandedModel(expandedModel === model.key ? null : model.key)}
                    className="p-2 text-white/50 hover:text-white hover:bg-white/10 rounded-lg transition-all"
                    title="Details"
                  >
                    {expandedModel === model.key ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => handleToggle(model.key)}
                    className={`p-2 rounded-lg transition-all ${
                      model.is_active 
                        ? 'text-[#10B981] hover:bg-[#10B981]/20' 
                        : 'text-red-400 hover:bg-red-500/20'
                    }`}
                    title={model.is_active ? 'Deaktivieren' : 'Aktivieren'}
                  >
                    <Power className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(model.key)}
                    className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-all"
                    title="Löschen"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedModel === model.key && (
                <div className="border-t border-white/10 p-4 bg-black/20">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-white/50">Key:</span>
                      <span className="text-white ml-2 font-mono">{model.key}</span>
                    </div>
                    <div>
                      <span className="text-white/50">USD:</span>
                      <span className="text-white ml-2">${model.price_usd?.toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-white/50">Beschreibung:</span>
                      <span className="text-white ml-2">{model.description || '-'}</span>
                    </div>
                    {model.params && Object.keys(model.params).length > 0 && (
                      <div className="md:col-span-2 lg:col-span-3">
                        <span className="text-white/50">Parameter:</span>
                        <pre className="mt-2 p-3 bg-black/50 rounded-lg text-white/70 text-xs overflow-x-auto">
                          {JSON.stringify(model.params, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-end mt-4 pt-4 border-t border-white/10">
                    <button
                      onClick={() => setEditingModel(model)}
                      className="flex items-center gap-2 px-4 py-2 text-[#10B981] hover:bg-[#10B981]/20 rounded-xl transition-all"
                    >
                      <Edit2 className="w-4 h-4" />
                      Bearbeiten
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Edit Modal */}
      {editingModel && (
        <EditModelModal
          model={editingModel}
          groups={groups}
          onSave={(updates) => handleUpdate(editingModel.key, updates)}
          onClose={() => setEditingModel(null)}
        />
      )}
    </div>
  );
}


// Edit Modal Component
function EditModelModal({ model, groups, onSave, onClose }) {
  const [formData, setFormData] = useState({
    label: model.label,
    price_usd: model.price_usd,
    price_snaps: model.price_snaps,
    description: model.description || '',
    group: model.group,
    recommended: model.recommended,
    is_active: model.is_active,
    is_nsfw: model.is_nsfw,
    admin_only: model.admin_only,
    needs_end_frame: model.needs_end_frame,
    params: model.params || {}
  });
  const [paramsText, setParamsText] = useState(JSON.stringify(model.params || {}, null, 2));

  const handleSave = () => {
    try {
      const params = JSON.parse(paramsText);
      onSave({ ...formData, params });
    } catch (e) {
      alert('Invalid JSON in parameters');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-black border border-white/20 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-xl font-semibold text-white">✏️ Modell bearbeiten: {model.key}</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="text-white/50 text-sm mb-4 font-mono bg-white/5 p-2 rounded">
            {model.model_id}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-white/70 text-sm mb-1">Label</label>
              <input
                type="text"
                value={formData.label}
                onChange={(e) => setFormData({...formData, label: e.target.value})}
                className="w-full px-4 py-3 bg-black border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none"
              />
            </div>
            
            <div>
              <label className="block text-white/70 text-sm mb-1">Gruppe</label>
              <select
                value={formData.group}
                onChange={(e) => setFormData({...formData, group: e.target.value})}
                className="w-full px-4 py-3 bg-black border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none"
              >
                {groups.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-white/70 text-sm mb-1">Preis (USD)</label>
              <input
                type="number"
                step="0.01"
                value={formData.price_usd}
                onChange={(e) => setFormData({...formData, price_usd: parseFloat(e.target.value)})}
                className="w-full px-4 py-3 bg-black border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-white/70 text-sm mb-1">Preis (Snaps)</label>
              <input
                type="number"
                value={formData.price_snaps}
                onChange={(e) => setFormData({...formData, price_snaps: parseInt(e.target.value)})}
                className="w-full px-4 py-3 bg-black border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-white/70 text-sm mb-1">Beschreibung</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-4 py-3 bg-black border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none"
            />
          </div>

          <div className="flex flex-wrap gap-4">
            {[
              {key: 'recommended', label: '⭐ Empfohlen'},
              {key: 'is_active', label: '✅ Aktiv'},
              {key: 'is_nsfw', label: '🔞 NSFW'},
              {key: 'admin_only', label: '🔒 Nur Admin'},
              {key: 'needs_end_frame', label: '🖼️ End-Frame'}
            ].map(toggle => (
              <label key={toggle.key} className="flex items-center gap-2 text-white/70 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData[toggle.key]}
                  onChange={(e) => setFormData({...formData, [toggle.key]: e.target.checked})}
                  className="rounded border-white/20 bg-black text-[#10B981] focus:ring-[#10B981]"
                />
                {toggle.label}
              </label>
            ))}
          </div>

          <div>
            <label className="block text-white/70 text-sm mb-1">Parameter (JSON)</label>
            <textarea
              value={paramsText}
              onChange={(e) => setParamsText(e.target.value)}
              rows={8}
              className="w-full px-4 py-3 bg-black border border-white/20 rounded-xl text-white font-mono text-sm focus:border-[#10B981] focus:outline-none"
            />
          </div>
        </div>

        <div className="p-6 border-t border-white/10 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-white/70 hover:text-white transition-all"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-6 py-2 bg-[#10B981] text-black font-semibold rounded-xl hover:bg-[#10B981]/90 transition-all"
          >
            <Save className="w-5 h-5" />
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
}
